import {isCareViewAllowed} from "./careEdition";
// GuidedTour.js
// =============================================================================
// A real, in-app guided walkaround of the management platform's top-level
// navigation: a dimmed backdrop, a spotlight cut around one real rail button
// at a time, and a tooltip explaining it, with Back / Next / Skip.
//
// Launched from "Take the tour" at the foot of the left rail (see App.js),
// and auto-launched once per browser on first login.
//
// Copy is deliberately close to Navigator.js's own CONTEXT entries for the
// same route, so the static reference panel and this walkaround never say
// two different things about what a screen does.
//
// SCOPE: this first pass covers the top-level rail destinations only (the
// buttons that are always visible, not the items inside a flyout like
// Delivery/Stock/Services/Admin, and not cards inside a screen like Head
// Office). It finds each target by a `data-tour="…"` attribute already
// sitting in the DOM. Extend TOUR_STEPS below — and add a matching
// `data-tour` prop to the element — to go deeper later.
// =============================================================================

import React, { useEffect, useRef, useState } from "react";

const PAD = 6;

export const TOUR_STEPS = [
  {
    id: "welcome",
    title: "Take a quick look around",
    body: "This is a short walkaround of the main sections of the management platform. Use Next / Back to move through it, or Skip tour at any point.",
  },
  {
    id: "head_office",
    selector: "nav-head_office",
    view: "head_office",
    title: "Head Office",
    body: "The live fleet dashboard \u2014 where each branch stands right now. Review SOPs, appraisals, training, daily checks, MHRA alerts and the accident book.",
  },
  {
    id: "mockup",
    selector: "nav-mockup",
    view: "mockup",
    title: "Site Activity",
    body: "Pick a date range and see aggregated activity totals across every site.",
  },
  {
    id: "regulatory_compliance",
    selector: "nav-regulatory_compliance",
    view: "regulatory_compliance",
    title: "Regulatory Compliance",
    body: "Fleet compliance against GPhC standards and statutory duties, tabbed by area. Drill from an area down to per-branch and per-item completion, or record alternative evidence where a branch keeps paper records.",
  },
  {
    id: "dashpanels",
    selector: "nav-dashpanels",
    view: "dashpanels",
    title: "Custom Live monitoring panels",
    body: "Describe a monitoring panel in plain English and have it built, refreshed on the interval you choose. Arrange panels and save the layout as a named dashboard.",
  },
  {
    id: "business_insights",
    selector: "nav-business_insights",
    view: "business_insights",
    title: "Business Insights",
    body: "Monthly income, cost and contribution per branch, with fleet comparisons, trends and recommendations.",
  },
  {
    id: "communications",
    selector: "nav-communications",
    view: "communications",
    title: "Communications",
    body: "Every message the platform sends, gathered in one place \u2014 including the Action centre for fleet-wide messages that need acknowledging, and staff notifications.",
  },
  {
    id: "settings",
    selector: "nav-settings",
    title: "Settings",
    body: "Opens a dialog with three tabs: Organisation Settings, Site Settings, and the financial Configurables used by Business Insights. This step won't open it for you \u2014 click it yourself whenever you're ready.",
  },
  {
    id: "navigator",
    selector: "nav-navigator",
    title: "Show navigator",
    body: "Opens a small floating panel that explains where you are and answers \u201chow do I\u2026\u201d questions. It stays open while you navigate, so you can read the answer and follow it at the same time. Come back to \u201cTake the tour\u201d here any time to replay this walkaround.",
  },
].filter(step => !step.view || isCareViewAllowed(step.view));

function computeTipStyle(rect, tipSize) {
  const margin = 14;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (!rect) {
    return {
      top: Math.max(margin, (vh - tipSize.height) / 2),
      left: Math.max(margin, (vw - tipSize.width) / 2),
    };
  }

  // Prefer to the right of the target; fall back to below, then above, then left.
  let left = rect.right + margin;
  let top = rect.top;

  if (left + tipSize.width > vw - margin) {
    left = rect.left;
    top = rect.bottom + margin;
    if (top + tipSize.height > vh - margin) {
      top = rect.top - tipSize.height - margin;
      if (top < margin) {
        top = margin;
        left = Math.max(margin, rect.left - tipSize.width - margin);
      }
    }
  }

  top = Math.min(Math.max(top, margin), vh - tipSize.height - margin);
  left = Math.min(Math.max(left, margin), vw - tipSize.width - margin);
  return { top, left };
}

export default function GuidedTour({ goTo, onFinish }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const tipRef = useRef(null);
  const [tipSize, setTipSize] = useState({ width: 320, height: 160 });

  const step = TOUR_STEPS[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === TOUR_STEPS.length - 1;

  // Navigate to the step's view as soon as it becomes current, so the real
  // screen is showing behind the spotlight rather than whatever was open
  // before the tour started.
  useEffect(() => {
    if (step.view && typeof goTo === "function") {
      goTo(step.view);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex]);

  // Poll for the target element every frame rather than once: moving
  // between views unmounts/remounts most of the app, so a target can appear
  // (or disappear) a few renders after the step itself changes.
  useEffect(() => {
    let raf;
    const tick = () => {
      if (step.selector) {
        const el = document.querySelector(`[data-tour="${step.selector}"]`);
        if (el) {
          const r = el.getBoundingClientRect();
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        } else {
          setRect(null);
        }
      } else {
        setRect(null);
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [stepIndex, step.selector]);

  useEffect(() => {
    if (tipRef.current) {
      const r = tipRef.current.getBoundingClientRect();
      if (r.width && r.height && (r.width !== tipSize.width || r.height !== tipSize.height)) {
        setTipSize({ width: r.width, height: r.height });
      }
    }
  });

  const notFound = !!step.selector && !rect;
  const hasSpotlight = !!step.selector && !!rect;
  const tipStyle = computeTipStyle(rect, tipSize);

  const next = () => {
    if (isLast) { onFinish(); return; }
    setStepIndex(i => i + 1);
  };
  const back = () => setStepIndex(i => Math.max(0, i - 1));

  return (
    <>
      {/* Backdrop \u2014 dims everything and absorbs clicks so the app underneath
          can't be poked mid-tour. The spotlight below paints its "hole" with
          a huge box-shadow rather than a real cut-out, so it sits on top of
          this rather than instead of it. */}
      <div
        style={{
          position: "fixed", inset: 0, zIndex: 2147483000,
          background: hasSpotlight ? "transparent" : "rgba(15, 23, 42, 0.55)",
        }}
      />

      {hasSpotlight && (
        <div
          style={{
            position: "fixed",
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            borderRadius: 8,
            boxShadow: "0 0 0 9999px rgba(15, 23, 42, 0.55)",
            border: "2px solid #0E8CFF",
            transition: "top .15s ease, left .15s ease, width .15s ease, height .15s ease",
            pointerEvents: "none",
            zIndex: 2147483001,
          }}
        />
      )}

      <div
        ref={tipRef}
        role="dialog"
        aria-label="Guided tour"
        style={{
          position: "fixed",
          top: tipStyle.top,
          left: tipStyle.left,
          width: 320,
          maxWidth: "calc(100vw - 20px)",
          background: "#fff",
          borderRadius: 10,
          boxShadow: "0 12px 32px rgba(16,24,40,0.22)",
          border: "1px solid #F0F0F0",
          padding: 16,
          zIndex: 2147483002,
          fontFamily: "inherit",
          transition: "top .15s ease, left .15s ease",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
          <div style={{ fontSize: 14.5, fontWeight: 700, color: "#262626" }}>{step.title}</div>
          <button
            onClick={onFinish}
            aria-label="Close tour"
            title="Close"
            style={{
              border: "none", background: "none", color: "#98a2b3", cursor: "pointer",
              fontSize: 16, lineHeight: 1, padding: 2, flexShrink: 0,
            }}
          >
            \u00d7
          </button>
        </div>

        <div style={{ fontSize: 13, color: "#434343", lineHeight: 1.55, marginTop: 8 }}>
          {step.body}
        </div>

        {notFound && (
          <div style={{ fontSize: 12, color: "#AD4E00", background: "#fff7e6", border: "1px solid #FFE58F",
                         borderRadius: 8, padding: "6px 8px", marginTop: 10 }}>
            Can't find this on your screen right now \u2014 it may be hidden for your account, or the
            window may be too narrow. Moving on won't affect the rest of the tour.
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14 }}>
          <span style={{ fontSize: 11.5, color: "#98a2b3" }}>
            {stepIndex + 1} / {TOUR_STEPS.length}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            {!isFirst && (
              <button onClick={back} style={btnGhost}>Back</button>
            )}
            <button onClick={next} style={btnPrimary}>
              {isLast ? "Finish" : "Next"}
            </button>
          </div>
        </div>

        {!isLast && (
          <button onClick={onFinish} style={{ ...linkBtn, marginTop: 8 }}>
            Skip tour
          </button>
        )}
      </div>
    </>
  );
}

const btnPrimary = {
  border: "none", background: "#0E8CFF", color: "#fff", borderRadius: 7,
  padding: "6px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
};
const btnGhost = {
  border: "1px solid #d8dee8", background: "#fff", color: "#434343", borderRadius: 7,
  padding: "6px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
};
const linkBtn = {
  border: "none", background: "none", color: "#5D5D5D", fontSize: 12, cursor: "pointer",
  fontFamily: "inherit", padding: 0, textDecoration: "underline",
};
